import React from "react";
import type { ExposureReactionDossierCaptureV3 } from "@shared/exposureReactionDossier";

export type ExposureReactionDossierVersionSummary = {
  reportId: string;
  parentReportId: string | null;
  sequence: number;
  createdAt: string;
  status: string;
};

function exactDecimalLabel(value: string | null, unit: string) {
  if (value === null) return "Unavailable";
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return "Unavailable";
  const [, sign, whole, fraction = ""] = match;
  const places = Math.min(fraction.length, 8);
  const kept = fraction.slice(0, places).padEnd(places, "0");
  let scaled = BigInt(`${whole}${kept}` || "0");
  if ((fraction[places] ?? "0") >= "5") scaled += BigInt(1);
  const digits = scaled.toString().padStart(whole.length + places, "0");
  const integer = places === 0 ? digits : digits.slice(0, -places);
  const decimals = places === 0 ? "" : digits.slice(-places).replace(/0+$/, "");
  const formatted = `${sign && scaled !== BigInt(0) ? "-" : ""}${integer}${decimals ? `.${decimals}` : ""}`;
  return `${formatted} ${unit}`;
}

function metricLabel(value: number | null, unit: string, places: number) {
  if (value === null || !Number.isFinite(value)) return "Unavailable";
  return `${value.toFixed(places)} ${unit}`;
}

function stateLabel(value: string) {
  return value.replaceAll("-", " ");
}

export default function ExposureReactionDossierDisplay({
  capture,
  versions = [],
}: {
  capture: ExposureReactionDossierCaptureV3;
  versions?: ExposureReactionDossierVersionSummary[];
}) {
  const result = capture.result;
  const asset = capture.evidence.asset;
  const event = capture.evidence.event;

  return <section className="analytics-panel" aria-label="Saved exposure and market response evidence">
    <p className="home-kicker">EXPOSURE AND SAMPLED MARKET RESPONSE · DETERMINISTIC · NO MODEL CALLS</p>
    <h2>Potential exposure and separately observed market response</h2>
    <p>Assessment cutoff: {capture.question.asOf}. Event: {event.sourceTitle ?? "Source title unavailable"}. Provider source ID: {event.providerReportId} · {event.provider}. Retained revision: {event.reportId} · {event.revisionId}. Instrument {capture.definition.instrument.listingSymbol} ({capture.definition.instrument.venue} {capture.definition.instrument.productType}).</p>

    <div className="reports-detail-evidence">
      <h3>Exposure assessment</h3>
      <p><strong>Evidence status:</strong> {result.status === "partial" ? "Partial evidence" : "Insufficient evidence"}.</p>
      <p><strong>Event state:</strong> {stateLabel(result.eventState)}.</p>
      <p><strong>Point and event geometry:</strong> {stateLabel(result.exposure.geometry)}. <strong>Potential exposure state:</strong> {stateLabel(result.exposureState)}.</p>
      <p><strong>Relationship to the named entity:</strong> {stateLabel(result.exposure.relationship)}. <strong>Derivative relationship:</strong> {stateLabel(result.exposure.contractRelationship)}.</p>
      <p>{result.exposure.limitation}</p>
      <p>Facility point: {asset.displayName}, {asset.point.coordinates[1]}, {asset.point.coordinates[0]}. This is a published site point, not a facility footprint. Positional accuracy: {asset.positionalAccuracy === "unknown" ? "unknown" : `${asset.positionalAccuracy.metres} metres`}.</p>
      <p>Entity relationship is dated {asset.relationship.validFrom ?? "unknown"}{asset.relationship.validThrough ? ` through ${asset.relationship.validThrough}` : ""}; this does not establish current ownership or operations.</p>
      <p><strong>Loss:</strong> unavailable. {result.loss.reason.replaceAll("-", " ")}.</p>

      <h3>Separately observed sampled response</h3>
      <p><strong>Reaction state:</strong> {stateLabel(result.reactionState)}.</p>
      <p>{result.reaction.limitation}</p>
      <p>Anchor: {result.reaction.window.anchor ?? "Unavailable"}. Pre window: {result.reaction.window.preStart ?? "Unavailable"} to {result.reaction.window.preEnd ?? "Unavailable"}. Post window: {result.reaction.window.postStart ?? "Unavailable"} to {result.reaction.window.postEnd ?? "Unavailable"}.</p>
      <dl>
        <dt>Pre-window sampled midpoint</dt>
        <dd>{exactDecimalLabel(result.reaction.preMidpoint, capture.definition.instrument.quote)}{result.reaction.window.preEnd ? ` · ${result.reaction.window.preEnd}` : ""}</dd>
        <dt>Post-window sampled midpoint</dt>
        <dd>{exactDecimalLabel(result.reaction.postMidpoint, capture.definition.instrument.quote)}{result.reaction.window.postEnd ? ` · ${result.reaction.window.postEnd}` : ""}</dd>
        <dt>Quoted price-point difference</dt>
        <dd>{metricLabel(result.reaction.quotedPricePointDifference, capture.definition.instrument.quote, 8)} (not a return or forecast)</dd>
        <dt>Sampled midpoint change</dt>
        <dd>{metricLabel(result.reaction.sampledPercentageChange, "%", 2)}</dd>
        <dt>Sampled benchmark change</dt>
        <dd>{metricLabel(result.reaction.benchmarkPercentageChange, "%", 2)}</dd>
        <dt>Benchmark-relative difference</dt>
        <dd>{metricLabel(result.reaction.relativePercentagePoints, "percentage points", 2)}</dd>
        <dt>Fixed endpoint coverage</dt>
        <dd>{result.reaction.coverage.observedRows} of {result.reaction.coverage.expectedRows} expected rows</dd>
      </dl>
      {result.reaction.reasons.length > 0 && <ul>{result.reaction.reasons.map((reason, index) => <li key={`${index}-${reason}`}>{reason.replaceAll("-", " ")}</li>)}</ul>}
      {result.reaction.coverage.gaps.length > 0 && <>
        <h4>Observed coverage gaps</h4>
        <ul>{result.reaction.coverage.gaps.map((gap, index) => <li key={`${index}-${gap}`}>{gap}</li>)}</ul>
      </>}
      {result.missing.length > 0 && <>
        <h3>Unavailable evidence</h3>
        <ul>{result.missing.map((reason, index) => <li key={`${index}-${reason}`}>{reason.replaceAll("-", " ")}</li>)}</ul>
      </>}
      <h3>Saved version comparison</h3>
      {capture.parentOperands
        ? <p>Compared with saved report <a href={`/reports?id=${encodeURIComponent(capture.parentOperands.reportId)}`}>{capture.parentOperands.reportId}</a> using its frozen manifest and calculations.</p>
        : <p>{capture.changeExplanation.status === "first-version" ? "This is the first saved version. No earlier comparison is available." : "A prior comparison is unavailable."}</p>}
      {capture.changeExplanation.reasons.map((reason, index) => <p key={`${index}-${reason}`}>{reason.replaceAll("-", " ")}</p>)}
      <p>Exposure and market movement are reported separately. This assessment does not show financial materiality, loss, disruption, realized impact, or causation.</p>
    </div>

    {versions.length > 0 && <details className="reports-detail-evidence">
      <summary>Saved dossier versions ({versions.length})</summary>
      <ol>{versions.map((version) => <li key={version.reportId}>
        <a href={`/reports?id=${encodeURIComponent(version.reportId)}`}>Version {version.sequence}</a> · {version.status} · {version.createdAt}
        {version.parentReportId ? ` · follows ${version.parentReportId}` : " · first version"}
      </li>)}</ol>
    </details>}
  </section>;
}
